import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { ArrowSwapHorizontal } from "iconsax-react";
import { Button, DatePicker } from "@/shared/ui";
import type { RangeValue } from "@/shared/ui";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import { useFinancePage } from "../../hooks/useFinancePage";
import { useSubcategoryDrawer } from "../../hooks/useSubcategoryDrawer";
import { useBudgetRedistributeWizard } from "../../hooks/useBudgetRedistributeWizard";
import {
  BalanceOverview,
  SectionCards,
  CategoriesSection,
  SubcategoryDrawer,
  FinancePageSkeleton,
  CashFlowChart,
  ProfitLossChart,
  BudgetRedistributeWizard,
} from "./components";

const YEAR_SUFFIX: Record<string, string> = { ru: "год", kk: "жыл", en: "" };

export default function FinancePage() {
  const { t, i18n } = useTranslation("FinancePage");
  const locale = (i18n.language || "ru") as "ru" | "kk" | "en";
  const pageData = useFinancePage();
  const drawer = useSubcategoryDrawer();
  const { hasPermission } = useAuth();
  const canRedistribute = hasPermission(PERMISSIONS.PROJECTS.EDIT_PROJECT);
  const { isOpen: isWizardOpen, initialSourceId, open: openWizard, close: closeWizard } = useBudgetRedistributeWizard();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(
    function openRedistributeFromQuery() {
      const raw = searchParams.get("redistribute");
      if (!raw) return;
      if (canRedistribute) {
        const id = Number(raw);
        if (!Number.isNaN(id)) openWizard(id);
      }
      const next = new URLSearchParams(searchParams);
      next.delete("redistribute");
      setSearchParams(next, { replace: true });
    },
    [searchParams, canRedistribute, openWizard, setSearchParams],
  );

  const pageTitle = pageData.activeProjectName
    ? t("meta.titleProject", { project: pageData.activeProjectName })
    : t("meta.title");

  if (pageData.isBudgetFetching && !pageData.budgetData) {
    return (
      <>
        <title>{pageTitle}</title>
        <section className="flex-1">
          <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
          </div>
          <FinancePageSkeleton />
        </section>
      </>
    );
  }

  if (pageData.budgetError || !pageData.budgetData) {
    return (
      <>
        <title>{pageTitle}</title>
        <section className="flex-1">
          <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
          </div>
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="text-body-regular-lg content-base-secondary">
              {pageData.budgetError ? t("page.errorLoading") : t("page.noData")}
            </div>
            <Button variant="primary" onClick={pageData.refetchBudget}>
              {t("page.retry")}
            </Button>
          </div>
        </section>
      </>
    );
  }

  function handleDateRangeChange(value: RangeValue | Date | null) {
    if (value && typeof value === "object" && "start" in value) {
      pageData.setDateRange(value);
    }
  }

  function formatDateRangeDisplay(range: RangeValue): string {
    if (!range.start || !range.end) return "";
    const s = range.start;
    const e = range.end;
    if (s.getMonth() === 0 && s.getDate() === 1 && e.getMonth() === 11 && e.getDate() === 31 && s.getFullYear() === e.getFullYear()) {
      const suffix = YEAR_SUFFIX[locale] ?? "";
      return suffix ? `${s.getFullYear()} ${suffix}` : `${s.getFullYear()}`;
    }
    const fmt = (d: Date) => d.toLocaleDateString(locale === "kk" ? "kk-KZ" : locale === "en" ? "en-US" : "ru-RU");
    return `${fmt(s)} — ${fmt(e)}`;
  }

  return (
    <>
      <title>{pageTitle}</title>
      <section className="flex-1">
        <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5 gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-display-xs content-base-primary">{t("page.title")}</h1>
            <DatePicker
              mode="range"
              value={pageData.dateRange}
              onChange={handleDateRangeChange}
              locale={locale}
              hasPeriodPicker
              formatDisplayValue={formatDateRangeDisplay}
            />
          </div>
          {canRedistribute && (
            <Button size="lg" variant="primary" onClick={() => openWizard()}>
              <span className="flex items-center gap-2">
                <ArrowSwapHorizontal size={20} color="currentColor" />
                {t("redistribute.button")}
              </span>
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <BalanceOverview
            data={pageData.budgetData}
            balance={pageData.balance}
            dateRange={pageData.dateRange}
            onDateRangeChange={pageData.setDateRange}
          />

          {pageData.cashFlowData.isAvailable && (
            <div className="radius-3xl surface-base-fill py-5 px-6">
              <h2 className="text-body-bold-lg content-base-primary mb-4">{t("cashFlow.title")}</h2>
              <CashFlowChart data={pageData.cashFlowData.chartData} isLoading={pageData.cashFlowData.isLoading} />
            </div>
          )}

          <div className="radius-3xl surface-base-fill py-5 px-6">
            <h2 className="text-body-bold-lg content-base-primary mb-4">{t("profitLoss.title")}</h2>
            <ProfitLossChart
              data={pageData.profitLossData.chartData}
              isLoading={pageData.profitLossData.isLoading || pageData.isTimeSeriesBudgetLoading}
            />
          </div>

          <div className="radius-3xl surface-base-fill py-5 px-6 flex flex-col gap-5">
            <h2 className="text-body-bold-lg content-base-primary">{t("sections.title")}</h2>
            <SectionCards
              sections={pageData.budgetData.sections}
              activeSection={pageData.activeSection}
              onSectionClick={pageData.handleSectionClick}
              hidePlanned={pageData.hidePlanned}
            />

            {pageData.activeSection && (
              <CategoriesSection
                section={pageData.activeSection}
                expenseBreakdownData={pageData.expenseBreakdownData}
                isChartLoading={pageData.isTimeSeriesBudgetLoading}
                onSubcategoryClick={drawer.openDrawer}
                hidePlanned={pageData.hidePlanned}
              />
            )}
          </div>
        </div>
      </section>

      {drawer.openSubcategory && (
        <SubcategoryDrawer
          key={drawer.openSubcategory.id}
          subcategory={drawer.openSubcategory}
          selectedItem={drawer.selectedItem}
          isExpanded={drawer.isExpanded}
          onItemClick={drawer.selectItem}
          onCloseDetail={drawer.closeDetail}
          onClose={drawer.closeDrawer}
          hidePlanned={pageData.hidePlanned}
        />
      )}

      {isWizardOpen && (
        <BudgetRedistributeWizard
          initialSourceId={initialSourceId}
          onClose={closeWizard}
          onSuccess={closeWizard}
        />
      )}
    </>
  );
}
